/**
 * Engine-agnostic benchmark scenes. A scene is described once as a list of
 * abstract body specs, then built into *both* a Fast2D world and a Matter.js
 * engine with the identical layout — so any timing difference is the engine,
 * not the scene.
 */

export interface BodySpec {
	kind: "box" | "circle" | "triangle";
	x: number;
	y: number;
	/** Box dimensions (world units). */
	w?: number;
	h?: number;
	/** Circle radius (world units). */
	r?: number;
	/** Local-space vertices for polygon/triangle bodies (world units, centroid at origin). */
	verts?: [number, number][];
	isStatic?: boolean;
	friction?: number;
	restitution?: number;
}

export interface CameraSpec {
	x: number;
	y: number;
	scale: number;
}

export interface BenchScene {
	id: string;
	title: string;
	blurb: string;
	gravityY: number;
	camera: CameraSpec;
	/** Emit every body in the scene via `add`. Called once per engine. */
	build: (add: (spec: BodySpec) => void) => void;
}

/** Deterministic PRNG (mulberry32) so every run is identical across engines. */
function rng(seed: number): () => number {
	let a = seed >>> 0;
	return () => {
		a = (a + 0x6d2b79f5) | 0;
		let t = Math.imul(a ^ (a >>> 15), 1 | a);
		t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
		return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
	};
}

/** A wide static floor with its top surface at world y = 0. */
function floor(add: (s: BodySpec) => void, width = 60): void {
	add({ kind: "box", x: 0, y: 0.5, w: width, h: 1, isStatic: true, friction: 0.8 });
}

/**
 * Local-space vertices for an upward-pointing triangle, centroid at the origin.
 * `s` is the half-height; the triangle is ~2s tall and ~1.73s wide. Wound with a
 * positive signed area (Box2D/p2/Fast2D "counter-clockwise" in their y-up math
 * convention; visually clockwise here since y is down) so every engine accepts
 * it as a valid convex polygon — p2-es in particular throws on the other winding.
 */
function triVerts(s: number): [number, number][] {
	return [
		[0, -s],
		[s * 0.866, s * 0.5],
		[-s * 0.866, s * 0.5],
	];
}

export const benchScenes: BenchScene[] = [
	{
		id: "pyramid",
		title: "Box pyramid",
		blurb: "A tall pyramid of boxes — the classic stacking-solver stress test.",
		gravityY: 9.81,
		camera: { x: 0, y: -6, scale: 16 },
		build(add) {
			floor(add);
			const rows = 18;
			const size = 0.8;
			const gap = 0.02;
			for (let r = 0; r < rows; r++) {
				const count = rows - r;
				const y = -size / 2 - r * (size + gap);
				const startX = -((count - 1) * (size + gap)) / 2;
				for (let c = 0; c < count; c++) {
					add({
						kind: "box",
						x: startX + c * (size + gap),
						y,
						w: size,
						h: size,
						friction: 0.6,
					});
				}
			}
		},
	},
	{
		id: "pile",
		title: "Circle pile",
		blurb: "Hundreds of circles poured into a bin — broadphase + contact churn.",
		gravityY: 9.81,
		camera: { x: 0, y: -6, scale: 14 },
		build(add) {
			floor(add, 28);
			// Side walls forming a bin.
			add({ kind: "box", x: -13, y: -6, w: 1, h: 14, isStatic: true });
			add({ kind: "box", x: 13, y: -6, w: 1, h: 14, isStatic: true });
			const r = rng(1337);
			for (let i = 0; i < 400; i++) {
				add({
					kind: "circle",
					x: -11 + r() * 22,
					y: -2 - i * 0.18,
					r: 0.28 + r() * 0.14,
					friction: 0.4,
					restitution: 0.05,
				});
			}
		},
	},
	{
		id: "mixed",
		title: "Mixed pit",
		blurb: "Boxes, circles, and triangles tumbling together — mixed narrowphase paths.",
		gravityY: 9.81,
		camera: { x: 0, y: -6, scale: 14 },
		build(add) {
			floor(add, 28);
			add({ kind: "box", x: -13, y: -6, w: 1, h: 14, isStatic: true });
			add({ kind: "box", x: 13, y: -6, w: 1, h: 14, isStatic: true });
			const r = rng(7);
			for (let i = 0; i < 300; i++) {
				const x = -11 + r() * 22;
				const y = -2 - i * 0.22;
				const pick = r();
				if (pick < 0.34) {
					add({ kind: "circle", x, y, r: 0.3 + r() * 0.18, friction: 0.5 });
				} else if (pick < 0.67) {
					const s = 0.5 + r() * 0.4;
					add({ kind: "box", x, y, w: s, h: s, friction: 0.5 });
				} else {
					const s = 0.35 + r() * 0.25;
					add({ kind: "triangle", x, y, verts: triVerts(s), friction: 0.5 });
				}
			}
		},
	},
];
